const CAMPAIGN_HOST_SELECTOR = '[data-storeeel-campaign-id]';
const MODAL_HOST_SELECTOR = '[data-storeeel-modal-host="true"]';
const BRIDGE_SELECTOR_RE = /storeeel|swiper|slide-media-wrapper|arrow-icon|GroupButtonFrame|cross-close-button-parent|pause-desktop|start-desktop|volume-desktop|sharing-desktop/;
const nativeDocumentQuerySelector = Document.prototype.querySelector;
const nativeDocumentQuerySelectorAll = Document.prototype.querySelectorAll;
const nativeElementQuerySelector = Element.prototype.querySelector;
const nativeElementQuerySelectorAll = Element.prototype.querySelectorAll;
const nativeNodeInsertBefore = Node.prototype.insertBefore;

let queryBridgeInstalled = false;

export const isStoreeelShadowDomEnabled = () => true;

export const applyImportantStyle = (element, styles = {}) => {
  Object.entries(styles).forEach(([property, value]) => {
    if (value == null || value === '') return;
    if (
      element.style.getPropertyValue(property) === value
      && element.style.getPropertyPriority(property) === 'important'
    ) return;
    element.style.setProperty(property, value, 'important');
  });
};

const shouldBridgeSelector = (selector) => (
  typeof selector === 'string' && BRIDGE_SELECTOR_RE.test(selector)
);

const createStaticElementList = (elements) => {
  const list = Array.from(elements);
  Object.defineProperty(list, 'item', {
    value: (index) => list[index] || null,
    enumerable: false,
  });
  return list;
};

export const installStoreeelShadowQueryBridge = () => {
  if (queryBridgeInstalled) return;
  queryBridgeInstalled = true;

  Document.prototype.querySelector = function storeeelQuerySelector(selector) {
    const found = nativeDocumentQuerySelector.call(this, selector);
    if (found || this !== document || !shouldBridgeSelector(selector)) return found;

    for (const shadowRoot of getStoreeelShadowRoots()) {
      const shadowFound = shadowRoot.querySelector(selector);
      if (shadowFound) return shadowFound;
    }

    return null;
  };

  Document.prototype.querySelectorAll = function storeeelQuerySelectorAll(selector) {
    const found = nativeDocumentQuerySelectorAll.call(this, selector);
    if (this !== document || found.length > 0 || !shouldBridgeSelector(selector)) return found;

    const shadowFound = [];
    for (const shadowRoot of getStoreeelShadowRoots()) {
      shadowRoot.querySelectorAll(selector).forEach((element) => {
        if (!shadowFound.includes(element)) shadowFound.push(element);
      });
    }

    return shadowFound.length > 0 ? createStaticElementList(shadowFound) : found;
  };
};

const installStoreeelHostQueryBridge = (host) => {
  if (!host || host.__storeeelHostQueryBridgeInstalled) return;

  Object.defineProperty(host, '__storeeelHostQueryBridgeInstalled', {
    value: true,
    configurable: true,
  });

  host.querySelector = function storeeelHostQuerySelector(selector) {
    const found = nativeElementQuerySelector.call(this, selector);
    if (found || !shouldBridgeSelector(selector)) return found;
    return this.shadowRoot?.querySelector(selector) || null;
  };

  host.querySelectorAll = function storeeelHostQuerySelectorAll(selector) {
    const found = nativeElementQuerySelectorAll.call(this, selector);
    if (found.length > 0 || !shouldBridgeSelector(selector)) return found;
    return this.shadowRoot?.querySelectorAll(selector) || found;
  };

  host.insertBefore = function storeeelHostInsertBefore(newNode, referenceNode) {
    if (referenceNode && this.shadowRoot?.contains(referenceNode) && referenceNode.parentNode) {
      // 既存カスタムHTMLがhost.insertBefore(newNode, shadowNode)してもShadow内へ挿入できるようにする
      return nativeNodeInsertBefore.call(referenceNode.parentNode, newNode, referenceNode);
    }
    return nativeNodeInsertBefore.call(this, newNode, referenceNode);
  };
};

export const getStoreeelCampaignHost = (campaignId) => {
  if (campaignId == null) return null;
  return nativeDocumentQuerySelector.call(
    document,
    `${CAMPAIGN_HOST_SELECTOR}[data-storeeel-campaign-id="${campaignId}"]`
  );
};

export const getStoreeelCampaignRoot = (campaignId) => {
  return getStoreeelCampaignHost(campaignId)?.shadowRoot || null;
};

export const getStoreeelModalHost = () => nativeDocumentQuerySelector.call(document, MODAL_HOST_SELECTOR);

export const getStoreeelModalRoot = () => getStoreeelModalHost()?.shadowRoot || null;

const getStoreeelShadowRoots = () => [
  ...Array.from(nativeDocumentQuerySelectorAll.call(document, CAMPAIGN_HOST_SELECTOR)).map((host) => host.shadowRoot).filter(Boolean),
  getStoreeelModalRoot(),
].filter(Boolean);

export const queryStoreeel = (selector, root = null) => {
  if (root?.querySelector) {
    const found = root.querySelector(selector);
    if (found) return found;
  }

  for (const shadowRoot of getStoreeelShadowRoots()) {
    const found = shadowRoot.querySelector(selector);
    if (found) return found;
  }

  // 移行期間は Light DOM 描画・既存テストとの互換を維持する
  return document.querySelector(selector);
};

export const queryStoreeelAll = (selector, root = null) => {
  const results = [];
  const pushUnique = (elements) => {
    elements.forEach((element) => {
      if (!results.includes(element)) results.push(element);
    });
  };

  if (root?.querySelectorAll) {
    pushUnique(Array.from(root.querySelectorAll(selector)));
  } else {
    getStoreeelShadowRoots().forEach((shadowRoot) => {
      pushUnique(Array.from(shadowRoot.querySelectorAll(selector)));
    });
  }

  // 移行期間は Light DOM 描画・既存テストとの互換を維持する
  pushUnique(Array.from(document.querySelectorAll(selector)));
  return results;
};

export const createStoreeelCampaignHost = (campaign, { center = false } = {}) => {
  installStoreeelShadowQueryBridge();

  const host = document.createElement('div');
  host.id = `storeeel-campaign-${campaign.id}`;
  host.className = 'storeeel-campaign-container';
  host.dataset.storeeelCampaignId = String(campaign.id);
  installStoreeelHostQueryBridge(host);

  applyImportantStyle(host, {
    width: '100%',
    'max-width': '100%',
    'min-width': '0',
    height: 'auto',
    'max-height': 'none',
    'min-height': '0',
    'box-sizing': 'border-box',
    contain: 'inline-size',
    display: center ? 'flex' : 'block',
    'justify-content': center ? 'center' : null,
    position: 'relative',
    overflow: 'visible',
    visibility: 'visible',
    opacity: '1',
    transform: 'none',
    filter: 'none',
    'clip-path': 'none',
    'pointer-events': 'auto',
    isolation: 'isolate',
  });

  return host;
};

export const createStoreeelModalHost = () => {
  installStoreeelShadowQueryBridge();

  const existingHost = getStoreeelModalHost();
  if (existingHost) existingHost.remove();

  const host = document.createElement('div');
  host.dataset.storeeelModalHost = 'true';

  applyImportantStyle(host, {
    display: 'block',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    'min-width': '0',
    'min-height': '0',
    'max-width': 'none',
    'max-height': 'none',
    margin: '0',
    padding: '0',
    overflow: 'visible',
    visibility: 'visible',
    opacity: '1',
    transform: 'none',
    filter: 'none',
    'clip-path': 'none',
    'pointer-events': 'auto',
    'z-index': '9999999999',
    isolation: 'isolate',
  });

  return host;
};

const STOREEL_MODAL_BASE_STYLE = `
  :host {
    display: block;
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 9999999999;
    pointer-events: auto;
    isolation: isolate;
    font-family: inherit;
  }

  .storeeel-shadow-root {
    all: initial;
    display: block;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    font-family: inherit;
    color: #ffffff;
  }

  .storeeel-shadow-root *,
  .storeeel-shadow-root *::before,
  .storeeel-shadow-root *::after {
    box-sizing: border-box;
  }

  .storeeel-modal {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    max-width: none;
    max-height: none;
    margin: 0;
    padding: 0;
    overflow: hidden;
    z-index: 9999999999;
    background-color: rgba(39, 39, 39, 0.87);
    backdrop-filter: blur(6px);
    visibility: visible;
    filter: none;
    clip-path: none;
    pointer-events: auto;
    isolation: isolate;
  }

  .storeeel-modal .swiper {
    margin-left: auto;
    margin-right: auto;
    position: relative;
    overflow: hidden;
    list-style: none;
    padding: 0;
    z-index: 1;
    display: block;
  }

  .storeeel-modal .swiper-vertical > .swiper-wrapper {
    flex-direction: column;
  }

  .storeeel-modal .swiper-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    display: flex;
    transition-property: transform;
    transition-timing-function: var(--swiper-wrapper-transition-timing-function, initial);
    box-sizing: content-box;
  }

  .storeeel-modal .swiper-android .swiper-slide,
  .storeeel-modal .swiper-ios .swiper-slide,
  .storeeel-modal .swiper-wrapper {
    transform: translate3d(0px, 0, 0);
  }

  .storeeel-modal .swiper-horizontal {
    touch-action: pan-y;
  }

  .storeeel-modal .swiper-vertical {
    touch-action: pan-x;
  }

  .storeeel-modal .swiper-slide {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    position: relative;
    transition-property: transform;
    display: block;
  }

  .storeeel-modal .swiper-slide-invisible-blank {
    visibility: hidden;
  }

  .storeeel-modal .swiper-backface-hidden .swiper-slide {
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  .storeeel-modal .swiper-3d .swiper-wrapper {
    transform-style: preserve-3d;
  }

  .storeeel-modal .swiper-3d {
    perspective: 1200px;
  }

  .storeeel-modal .swiper-3d .swiper-slide,
  .storeeel-modal .swiper-3d .swiper-cube-shadow {
    transform-style: preserve-3d;
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow,
  .storeeel-modal .swiper-3d .swiper-slide-shadow-left,
  .storeeel-modal .swiper-3d .swiper-slide-shadow-right,
  .storeeel-modal .swiper-3d .swiper-slide-shadow-top,
  .storeeel-modal .swiper-3d .swiper-slide-shadow-bottom {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 10;
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow {
    background: rgba(0, 0, 0, 0.15);
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow-left {
    background-image: linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow-right {
    background-image: linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow-top {
    background-image: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
  }

  .storeeel-modal .swiper-3d .swiper-slide-shadow-bottom {
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
  }

  .storeeel-modal .swiper.swiper-cube {
    overflow: visible;
  }

  .storeeel-modal .swiper-cube .swiper-slide {
    pointer-events: none;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    z-index: 1;
    visibility: hidden;
    transform-origin: 0 0;
    width: 100%;
    height: 100%;
  }

  .storeeel-modal .swiper-cube .swiper-slide .swiper-slide {
    pointer-events: none;
  }

  .storeeel-modal .swiper-cube.swiper-rtl .swiper-slide {
    transform-origin: 100% 0;
  }

  .storeeel-modal .swiper-cube .swiper-slide-active,
  .storeeel-modal .swiper-cube .swiper-slide-active .swiper-slide-active {
    pointer-events: auto;
  }

  .storeeel-modal .swiper-cube .swiper-slide-active,
  .storeeel-modal .swiper-cube .swiper-slide-next,
  .storeeel-modal .swiper-cube .swiper-slide-prev {
    pointer-events: auto;
    visibility: visible;
  }

  .storeeel-modal .swiper-cube .swiper-cube-shadow {
    position: absolute;
    left: 0;
    bottom: 0px;
    width: 100%;
    height: 100%;
    opacity: 0.6;
    z-index: 0;
  }

  .storeeel-modal .swiper-cube .swiper-cube-shadow:before {
    content: '';
    background: #000;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    filter: blur(50px);
  }

  .storeeel-modal .swiper-cube .swiper-slide-next + .swiper-slide {
    pointer-events: auto;
    visibility: visible;
  }

  .storeeel-modal .swiper-cube .swiper-slide-shadow-cube.swiper-slide-shadow-top,
  .storeeel-modal .swiper-cube .swiper-slide-shadow-cube.swiper-slide-shadow-bottom,
  .storeeel-modal .swiper-cube .swiper-slide-shadow-cube.swiper-slide-shadow-left,
  .storeeel-modal .swiper-cube .swiper-slide-shadow-cube.swiper-slide-shadow-right {
    z-index: 0;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  .storeeel-modal img.storeeel-media-image,
  .storeeel-modal video.storeeel-movie {
    display: block;
    max-width: none;
    box-sizing: border-box;
  }

  .storeeel-ad-badge {
    position: absolute;
    top: 16px;
    left: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.40);
    z-index: 20;
    pointer-events: auto;
    cursor: pointer;
    user-select: none;
  }

  .storeeel-ad-badge__text {
    color: #fff;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.08em;
  }

  .storeeel-ad-badge.storeeel-ad-badge--mobile {
    position: fixed;
    top: 48px;
    left: 4%;
    z-index: 999999;
    transform: none;
  }

  .storeeel-ad-disclosure {
    display: none;
    position: fixed;
    max-width: min(360px, calc(100vw - 24px));
    padding: 14px 44px 14px 16px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.92);
    color: #fff;
    z-index: 1000000;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(4px);
    line-height: 1.5;
    box-sizing: border-box;
  }

  .storeeel-ad-disclosure.storeeel-ad-disclosure--open {
    display: block;
  }

  .storeeel-ad-disclosure__text {
    margin: 0 0 12px;
    padding-right: 2px;
    white-space: pre-wrap;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.6;
  }

  .storeeel-ad-disclosure__link {
    color: #7fc5ff;
    font-size: 13px;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .storeeel-ad-disclosure__close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    border: 0;
    background: transparent;
    color: #fff;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  .storeeel-movie-select_banner {
    height: 3px;
    background-color: rgba(255, 255, 255, 0.2);
    margin: 0 3px;
    position: relative;
    overflow: hidden;
    width: var(--banner-width, calc(100% / 1));
    display: block;
  }

  .storeeel-movie-select_banner::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background-color: white;
    width: 0%;
    animation-play-state: running;
  }

  .storeeel-movie-select_banner.active::before {
    animation: progressBarAnimation var(--duration, 3s) linear forwards;
  }

  .storeeel-movie-select_banner.completed::before {
    width: 100%;
    background-color: white;
    animation: none;
  }

  .storeeel-movie-select_banner.paused::before {
    animation-play-state: paused !important;
  }

  @keyframes progressBarAnimation {
    0% { width: 0%; }
    100% { width: 100%; }
  }

  .slide-media-wrapper.storeeel-media-loading::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.06) 8%,
      rgba(255, 255, 255, 0.18) 18%,
      rgba(255, 255, 255, 0.06) 33%
    );
    background-size: 200% 100%;
    animation: storeeel-shimmer 1.1s linear infinite;
    z-index: 2;
    pointer-events: none;
  }

  @keyframes storeeel-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .storeeel-modal video::-webkit-media-controls-download-button,
  .storeeel-modal video::-internal-media-controls-download-button,
  .storeeel-modal video::-webkit-media-controls-overlay-play-button,
  .storeeel-modal video::-webkit-media-controls-fullscreen-button,
  .storeeel-modal video::-webkit-media-controls-enter-fullscreen-button,
  .storeeel-modal video::-webkit-media-controls-exit-fullscreen-button,
  .storeeel-modal video::-webkit-media-controls-picture-in-picture-button {
    display: none !important;
  }
`;

export const createStoreeelModalShadowRootContainer = (host) => {
  const shadowRoot = host.shadowRoot || host.attachShadow({ mode: 'open' });
  shadowRoot.innerHTML = '';

  const style = document.createElement('style');
  style.dataset.storeeelShadowStyle = 'modal-base';
  style.textContent = STOREEL_MODAL_BASE_STYLE;

  const container = document.createElement('div');
  container.className = 'storeeel-shadow-root';
  container.dataset.storeeelShadowScope = 'modal';

  shadowRoot.appendChild(style);
  shadowRoot.appendChild(container);
  return { shadowRoot, container };
};

export const createStoreeelShadowRootContainer = (host, { scope = 'campaign', center = false } = {}) => {
  const shadowRoot = host.shadowRoot || host.attachShadow({ mode: 'open' });
  shadowRoot.innerHTML = '';

  const style = document.createElement('style');
  style.dataset.storeeelShadowStyle = 'base';
  style.textContent = `
    :host {
      display: block;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      contain: inline-size;
      font-family: inherit;
    }

    .storeeel-shadow-root {
      all: initial;
      --Black-100: #272727;
      --Black-90: rgba(39, 39, 39, 0.90);
      --White-100: #ffffff;
      --collection-1-white-100: #ffffff;
      --storeeel-group-name-color: #272727;
      --storeeel-group-name-font-size: 12px;
      --storeeel-group-name-font-weight: 500;
      --storeeel-circle-thumbnail-size: 80px;
      --storeeel-square-thumbnail-width: 182px;
      --storeeel-square-thumbnail-height: 324px;
      --storeeel-read-status-ring-size: 4px;
      --storeeel-unseen-border-color: #000000;
      --storeeel-seen-border-color: #A3A3A3;
      display: block;
      flex: 1 1 auto;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
      font-family: inherit;
      color: #272727;
    }

    .storeeel-shadow-root *,
    .storeeel-shadow-root *::before,
    .storeeel-shadow-root *::after {
      box-sizing: border-box;
    }

    .storeeel-shadow-root img,
    .storeeel-shadow-root video,
    .storeeel-shadow-root button {
      max-width: none;
      font: inherit;
    }

    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-download-button,
    .storeeel-shadow-root .storeeel-content-movie::-internal-media-controls-download-button,
    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-overlay-play-button,
    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-fullscreen-button,
    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-enter-fullscreen-button,
    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-exit-fullscreen-button,
    .storeeel-shadow-root .storeeel-content-movie::-webkit-media-controls-picture-in-picture-button {
      display: none !important;
    }
  `;

  const container = document.createElement('div');
  container.className = 'storeeel-shadow-root';
  container.dataset.storeeelShadowScope = scope;
  if (center) {
    // PC中央寄せをLight DOM時の外側コンテナと同じflexレイアウトで再現する
    container.style.display = 'flex';
    container.style.justifyContent = 'center';
  }

  shadowRoot.appendChild(style);
  shadowRoot.appendChild(container);
  return { shadowRoot, container };
};
